import React, { useState, useEffect } from "react";
import {
  Text,
  Badge,
  ActionIcon,
  Menu,
  Box,
  Image,
  Stack,
  Group,
} from "@mantine/core";
import { IconDotsVertical, IconPackage } from "@tabler/icons-react";
import { useProducts } from "../../context/ProductsContext";
import { searchAndPaginateProducts } from "../../utils/productSearchUtils";
import { StandardTable } from "../common/StandardTable";

export default function ProductsTable({ onProductClick }) {
  const {
    products,
    searchQuery,
    statusFilter,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    error,
  } = useProducts();

  const [searchResults, setSearchResults] = useState({
    products: [],
    total: 0,
    hasMore: false,
    currentPage: 1,
  });

  useEffect(() => {
    const performSearch = async () => {
      const results = await searchAndPaginateProducts(
        products,
        searchQuery,
        currentPage,
        pageSize,
        statusFilter,
      );
      setSearchResults(results);
    };

    performSearch();
  }, [products, searchQuery, statusFilter, currentPage, pageSize]);

  const getStockColor = (quantity, threshold) => {
    if (quantity <= threshold) {
      return "var(--brand-burnt-orange)";
    }
    return "var(--brand-lime-green)";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const columns = [
    {
      key: "product",
      label: "Product",
      render: (product) => (
        <Group gap="sm">
          <Image
            src={product.img_url || "https://placehold.co/50x50?text=No+Image"}
            fallbackSrc="https://placehold.co/50x50?text=No+Image"
            w={50}
            h={50}
            fit="contain"
            style={{
              border: "1px solid var(--mantine-color-gray-3)",
              borderRadius: "4px",
            }}
          />
          <Stack gap={2}>
            <Text size="sm" fw={500}>
              {product.name}
            </Text>
            <Text size="xs" c="dimmed">
              {product.company}
            </Text>
          </Stack>
        </Group>
      ),
    },
    {
      key: "category",
      label: "Category",
      render: (product) => (
        <Badge variant="light" size="sm">
          {product.category}
        </Badge>
      ),
    },
    {
      key: "barcode",
      label: "Barcode",
      render: (product) => (
        <Text size="sm" ff="monospace">
          {product.custom_id}
        </Text>
      ),
    },
    {
      key: "price",
      label: "Price",
      align: "right",
      render: (product) => (
        <Stack gap={2} align="flex-end">
          <Text size="sm" fw={500}>
            {formatCurrency(product.list_price)}
          </Text>
          <Text size="xs" c="dimmed">
            Cost: {formatCurrency(product.purchase_price)}
          </Text>
        </Stack>
      ),
    },
    {
      key: "stock",
      label: "Stock",
      render: (product) => (
        <Stack gap={2}>
          <Badge
            color={getStockColor(product.quantity, product.reorder_threshold)}
            variant="light"
            size="sm"
          >
            {product.quantity <= product.reorder_threshold
              ? "Low Stock"
              : "In Stock"}
          </Badge>
          <Text size="xs" c="dimmed">
            {product.quantity} units
          </Text>
        </Stack>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (product) => (
        <Badge
          color={
            product.status === "Active"
              ? "green"
              : product.status === "Inactive"
                ? "gray"
                : "red"
          }
          variant="light"
          size="sm"
        >
          {product.status}
        </Badge>
      ),
    },
    {
      key: "actions",
      label: "",
      width: 56,
      align: "right",
      render: (product) => (
        <Menu>
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={(e) => e.stopPropagation()}
            >
              <IconDotsVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconPackage size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                onProductClick(product);
              }}
            >
              View Details
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      ),
    },
  ];

  if (error) {
    return (
      <Box p="md" ta="center">
        <Text c="red">Error loading products: {error}</Text>
      </Box>
    );
  }

  return (
    <StandardTable
      columns={columns}
      data={searchResults.products}
      getRowKey={(p) => p.id}
      onRowClick={onProductClick}
      ariaLabel="Products"
      emptyState={
        <Stack gap="xs" align="center">
          <IconPackage size={48} color="var(--mantine-color-gray-4)" />
          <Text c="dimmed">
            {searchQuery || statusFilter !== "All"
              ? "No products match your search criteria"
              : "No products found"}
          </Text>
        </Stack>
      }
      pagination={{
        page: currentPage,
        pageSize,
        total: searchResults.total,
        onPageChange: setCurrentPage,
        onPageSizeChange: (size) => {
          setPageSize(size);
          setCurrentPage(1);
        },
        pageSizeOptions: ["10", "25", "50"],
        itemLabel: "products",
      }}
    />
  );
}
